import { fetchPlansItems as defaultFetchPlansItems } from '@atlassian/jira-nav-item-service/src/services/plans-item/index.tsx';
import type { PlansItems } from '@atlassian/jira-nav-item-service/src/services/plans-item/types.tsx';
import { type BaseUrl, toBaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type GetState,
	type SetState,
	createStore,
	createHook,
	type StoreActionApi,
} from '@atlassian/react-sweet-state';

type StoreConfig = {
	baseUrl: BaseUrl;
	fetchPlansItems: (baseUrl: BaseUrl) => Promise<PlansItems>;
};

type PublicState = {
	hasFetchedOnce: boolean;
	items: PlansItems | null;
	fetchError: Error | null;
	promise: Promise<PlansItems> | null;
	hasSuccessOnce: boolean;
	isFetching: boolean;
};

type State = StoreConfig & PublicState;

const handleFetch =
	() =>
	async ({ setState, getState }: { getState: GetState<State>; setState: SetState<State> }) => {
		const { fetchPlansItems, baseUrl, isFetching } = getState();
		if (!isFetching) {
			try {
				const promise = fetchPlansItems(baseUrl);

				setState({
					isFetching: true,
					fetchError: null,
					promise,
				});

				const items = await promise;

				setState({
					items,
					hasFetchedOnce: true,
					hasSuccessOnce: true,
					isFetching: false,
					fetchError: null,
					promise: null,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (fetchError: any) {
				setState({
					...getState(),
					fetchError,
					hasFetchedOnce: true,
					isFetching: false,
					promise: null,
				});
			}
		}
	};

const actions = {
	load:
		() =>
		async ({ dispatch }: StoreActionApi<State>) => {
			const promise = dispatch(handleFetch());
			await promise;
		},
} as const;

type Actions = typeof actions;

// Exported for testing
export const createPlansItemStore = ({
	storeName = 'plans-item-store',
	fetchPlansItems,
}: {
	storeName?: string;
	fetchPlansItems: (baseUrl: BaseUrl) => Promise<PlansItems>;
}) => {
	const initialState = {
		baseUrl: toBaseUrl(''),
		fetchPlansItems,
		hasFetchedOnce: false,
		hasSuccessOnce: false,
		items: null,
		fetchError: null,
		promise: null,
		isFetching: false,
	};

	const Store = createStore<State, Actions>({
		name: storeName,
		actions,
		initialState,
	});

	const selector = ({
		items,
		hasFetchedOnce,
		fetchError,
		promise,
		hasSuccessOnce,
		isFetching,
	}: PublicState) => ({
		items,
		hasFetchedOnce,
		hasSuccessOnce,
		fetchError,
		promise,
		isFetching,
	});

	const usePlansItems = createHook(Store, {
		selector,
	});

	return { usePlansItems };
};

export const { usePlansItems } = createPlansItemStore({
	fetchPlansItems: defaultFetchPlansItems,
});

export type UsePlansItems = typeof usePlansItems;
